import React, { useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate, Link, useLocation } from 'react-router-dom';
import { onAuthStateChanged } from 'firebase/auth';
import { auth } from './firebase/config';
import posthog from './analytics/posthog';
import LoginPage from './pages/Login/LoginPage';
import GoogleSignIn from './components/auth/GoogleSignIn';
import Dashboard from './pages/Dashboard/Dashboard';
import DashboardPage from './pages/Dashboard/DashboardPage';
import BrandEditPage from './pages/BrandEdit/BrandEditPage';
import DocumentProcessing from './pages/DocumentProcessing/DocumentProcessing';
import DeckSettingsPage from './pages/DeckSettings/DeckSettingsPage';
import CollectionSettingsPage from './pages/CollectionSettings/CollectionSettingsPage';
import GeneratedDecksPage from './pages/GeneratedDecks/GeneratedDecksPage';
import SettingsPage from './pages/Settings/SettingsPage';
import DocumentationPage from './pages/Documentation/DocumentationPage';
import SupportPage from './pages/Support/SupportPage';
import 'bootstrap/dist/css/bootstrap.min.css';
import './App.css';

// Track page views on route change
function PageViewTracker() {
  const location = useLocation();

  useEffect(() => {
    posthog.capture('$pageview', {
      $current_url: window.location.href,
    });
  }, [location.pathname]);

  return null;
}

// Navigation component to show tabs
function Navigation() {
  const location = useLocation();
  
  return (
    <ul className="nav nav-tabs mb-4">
      <li className="nav-item">
        <Link 
          className={`nav-link ${location.pathname === '/' ? 'active' : ''}`}
          to="/"
        >
          Dashboard
        </Link>
      </li>
      <li className="nav-item">
        <Link 
          className={`nav-link ${location.pathname === '/processing' ? 'active' : ''}`}
          to="/processing"
        >
          Document Processing
        </Link>
      </li>
    </ul>
  );
}

function App() {
  const [user, setUser] = useState(null);
  const [idToken, setIdToken] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (user) {
        const token = await user.getIdToken();
        setUser(user);
        setIdToken(token);
        // Identify user in PostHog
        posthog.identify(user.uid, {
          email: user.email,
          name: user.displayName,
        });
      } else {
        setUser(null);
        setIdToken(null);
        // Reset PostHog on logout
        posthog.reset();
      }
      setLoading(false);
    });

    return () => unsubscribe();
  }, []);

  const handleAuthStateChange = (user, token) => {
    setUser(user);
    setIdToken(token);
  };

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ height: '100vh' }}>
        <div className="spinner-border" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  return (
    <BrowserRouter>
      <PageViewTracker />
      <div className="App">
        {!user ? (
          <LoginPage />
        ) : (
          <Routes>
            {/* Main navigation pages */}
            <Route path="/" element={<DashboardPage />} />
            <Route path="/generated-decks" element={<GeneratedDecksPage />} />
            <Route path="/settings" element={<SettingsPage />} />
            <Route path="/documentation" element={<DocumentationPage />} />
            <Route path="/support" element={<SupportPage />} />

            {/* Legacy routes with card layout (keeping for now) */}
            <Route path="/processing" element={
              <div className="container mt-5">
                <div className="row justify-content-center">
                  <div className="col-md-8">
                    <div className="card">
                      <div className="card-header">
                        <h1 className="text-center mb-0">Product Training AI v1.0</h1>
                      </div>
                      <div className="card-body">
                        <div className="text-center mb-4">
                          <GoogleSignIn onAuthStateChange={handleAuthStateChange} />
                        </div>
                        <Navigation />
                        <DocumentProcessing />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            } />
            
            {/* Component Library / Test Page */}
            <Route path="/component-library" element={<DeckSettingsPage />} />
            
            {/* Detail pages */}
            <Route path="/brands/:brandId/edit" element={<BrandEditPage />} />
            <Route path="/deck-settings/:collectionId" element={<DeckSettingsPage />} />
            <Route path="/collection-settings/:collectionId" element={<CollectionSettingsPage />} />
            
            {/* Catch-all redirect */}
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        )}
      </div>
    </BrowserRouter>
  );
}

export default App;
